import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  postNamedCommand,
  postNamedQuery
} from '../service'
import { useAppContext } from '../redux/slices/appContext'
import { QUERY_KEYS } from './queryKeys'

export const useListIntegrations = () => {
  const { userId } = useAppContext()
  return useQuery({
    queryKey: [QUERY_KEYS.listIntegrations, userId],
    queryFn: async () => {
      const { data } = await postNamedQuery('levels', 'list-integrations', {})
      return data
    }
  })
}

export const useGetIntegrationBatchById = (batchId, options = {}) => {
  const includeTasks = options.includeTasks || false
  return useQuery({
    queryKey: [QUERY_KEYS.getIntegrationBatchById, batchId, includeTasks],
    queryFn: async () => {
      const { data } = await postNamedQuery('integrator', 'get-integration-batch-by-id', {
        batchId,
        includeTasks
      })
      return data
    }
  })
}

export const useListIntegrationDomains = (query = {}) => {
  const queryKey = query ? JSON.stringify(query) : '{}'
  return useQuery({
    queryKey: [QUERY_KEYS.listIntegrationDomains, queryKey],
    queryFn: async () => {
      const { data } = await postNamedQuery('integrator', 'list-integration-domains', query)
      return data
    }
  })
}

export const useGetIntegrationSourceByKey = (domain, sourceKey) => {
  return useQuery({
    queryKey: [QUERY_KEYS.getIntegrationSourceByKey, domain, sourceKey],
    queryFn: async () => {
      const { data } = await postNamedQuery('integrator', 'get-integration-source-by-key', {
        domain,
        sourceKey
      })
      return data
    }
  })
}

export const useGetIntegrationSourceById = (integrationSourceId) => {
  return useQuery({
    queryKey: [QUERY_KEYS.getIntegrationSourceByKey, integrationSourceId],
    queryFn: async () => {
      const { data } = await postNamedQuery('integrator', 'get-integration-source-by-id', {
        integrationSourceId
      })
      return data
    }
  })
}

export const useListIntegrationSources = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.listIntegrationSources],
    queryFn: async () => {
      const { data } = await postNamedQuery('integrator', 'list-integration-sources', {})
      return data
    }
  })
}

export const useGetIntegrationTableMetadata = (jobConfigurationId) => {
  return useQuery({
    queryKey: [QUERY_KEYS.getIntegrationTableMetadata, jobConfigurationId],
    queryFn: async () => {
      const { data } = await postNamedQuery('integrator', 'get-integration-table-metadata', {
        jobConfigurationId
      })
      return data
    }
  })
}

export const useQueryIntegrationTable = (jobConfigurationId, query) => {
  const queryKey = query ? JSON.stringify(query) : ''
  return useQuery({
    queryKey: [QUERY_KEYS.queryIntegrationTable, jobConfigurationId, queryKey],
    queryFn: async () => {
      const { data } = await postNamedQuery('integrator', 'query-integration-table', {
        jobConfigurationId,
        queryAst: query
      })
      return data
    }
  })
}

export const useSearchIntegrationJobs = (query = {}) => {
  const queryKey = query ? JSON.stringify(query) : ''
  return useQuery({
    queryKey: [QUERY_KEYS.searchIntegrationJobs, queryKey],
    queryFn: async () => {
      const { data } = await postNamedQuery('integrator', 'search-integration-jobs', query)
      return data
    }
  })
}

export const useSearchIntegrationSources = (query = {}, options = {}) => {
  const enabled = options?.enabled ?? true
  const queryKey = query ? JSON.stringify(query) : ''
  return useQuery({
    enabled,
    queryKey: [QUERY_KEYS.searchIntegrationSources, queryKey],
    queryFn: async () => {
      const { data } = await postNamedQuery('integrator', 'search-integration-sources', query)
      return data
    }
  })
}

export const useSearchIntegrationBatches = (query = {}, options = {}) => {
  const enabled = options?.enabled ?? true
  const queryKey = query ? JSON.stringify(query) : ''
  return useQuery({
    enabled,
    queryKey: [QUERY_KEYS.searchIntegrationBatches, queryKey],
    queryFn: async () => {
      const { data } = await postNamedQuery('integrator', 'search-integration-batches', query)
      return data
    }
  })
}

export const useSyncMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('integrator', 'sync', command)

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.searchIntegrationBatches], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useScanMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { domain, objects, arguments: args, connectionId } = command
      const { data } = await postNamedCommand('integrator', 'scan', {
        domain,
        objects,
        arguments: args,
        connectionId
      })

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.searchIntegrationBatches], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useCheckMigrationsMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { domain, objects, connectionId } = command
      const { data } = await postNamedCommand('integrator', 'check-migrations', {
        domain,
        objects,
        connectionId
      })

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.listIntegrationSources], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useModifyIntegrationSourceKeys = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { integrationSourceId, keys } = command
      const { data } = await postNamedCommand('integrator', 'modify-integration-source-keys', {
        integrationSourceId,
        keys
      })

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getIntegrationSourceByKey], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useModifyIntegrationJobFields = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { jobConfigurationId, fields } = command
      const { data } = await postNamedCommand('integrator', 'modify-integration-job-fields', {
        jobConfigurationId,
        fields
      })

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.searchIntegrationJobs], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useModifyIntegrationJobStatus = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('integrator', 'modify-integration-job-status', command)

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.searchIntegrationJobs], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useCreateIntegrationJob = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('integrator', 'create-integration-job', command)

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.searchIntegrationJobs], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getIntegrationSourceByKey], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useLatestJobExecutionStatus = (sourceKey, jobKey) => {
  return useQuery({
    queryKey: ['integrations/get-latest-job-execution-status', sourceKey, jobKey],
    queryFn: async () => {
      const { data } = await postNamedQuery('integrator', 'get-latest-job-execution-status', { sourceKey, jobKey })
      return data
    }
  })
}

export const orionSystem = {
  useConnectionInfo: () => {
    return useQuery({
      queryKey: ['integrations/get-orion-system-connection-info'],
      queryFn: async () => {
        const { data } = await postNamedQuery('integrator', 'get-orion-system-connection-info', {})
        return data
      }
    })
  },

  useOrionSystemAutoConfigure: () => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async () => {
        const { data } = await postNamedCommand('integrator', 'auto-configure-orion-system-integration', {})

        return data
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['integrations/get-orion-system-connection-info'], refetchType: 'all' }).catch(console.error)
      }
    })
  },

  useStartOrionSystemSync: () => {
    return useMutation({
      mutationFn: async ({ objects, syncStrategy }) => {
        const { data } = await postNamedCommand('integrator', 'start-orion-system-sync', { objects, syncStrategy })

        return data
      }
    })
  }
}

// Module-scoped "we already tried and the refresh token itself was dead"
// memo. After a `reauthRequired: true` response, future list-token reads in
// the same session won't waste another `/oauth/token` round-trip — they just
// surface the still-flagged row and let the UI prompt the user to reconnect.
// Cleared on a successful authorize (see `useAuthorizeUserConnectionToken`).
const refreshExhaustedForDomain = new Set()

// Wrap the `list-user-connection-tokens` read so that whenever it returns a
// row flagged `requiresReauth: true`, we attempt a silent server-side OAuth
// refresh in line BEFORE returning to the caller. The refresh lives inside
// the queryFn (rather than as a separate mutation per IntegrationAuthProvider
// instance) so react-query's queryKey deduplication naturally coalesces N
// concurrent callers into a single outbound refresh — i.e. a dashboard with
// 10 wealthbox tiles only triggers one refresh, not 10.
//
// Failure handling is intentionally non-fatal: if the refresh throws or
// returns `reauthRequired: true`, we return the (still-flagged) tokens so
// the existing IntegrationAuthGate reconnect path kicks in. We also memo
// the failure for the rest of the session so subsequent mounts don't keep
// re-hitting `/oauth/token` against a dead refresh token.
async function listUserConnectionTokensWithSilentRefresh (domain) {
  const { data } = await postNamedQuery('integrator', 'list-user-connection-tokens', {
    domain: domain || null
  })

  const tokens = data?.tokens || []
  const candidates = tokens.filter(t =>
    t.requiresReauth &&
    !refreshExhaustedForDomain.has(t.domain) &&
    (!domain || t.domain === domain)
  )

  if (candidates.length === 0) return data

  let anyRefreshed = false
  await Promise.all(candidates.map(async (token) => {
    try {
      const { data: result } = await postNamedCommand('integrator', 'refresh-user-connection-token', {
        domain: token.domain
      })
      if (result?.reauthRequired === true) {
        refreshExhaustedForDomain.add(token.domain)
      } else if (result?.success === true) {
        anyRefreshed = true
      }
    } catch (err) {
      // Transient error (network, integrator 5xx). Don't memo this - a later
      // mount can retry once integrator is healthy again. Use console.error
      // to match this file's convention (`.catch(console.error)` everywhere).
      console.error(`Silent refresh for ${token.domain} failed transiently:`, err?.message || err)
    }
  }))

  if (!anyRefreshed) return data

  // At least one refresh wrote a new token row — re-read so the caller sees
  // the cleared `requiresReauth` flag + the new expiration.
  const { data: refreshed } = await postNamedQuery('integrator', 'list-user-connection-tokens', {
    domain: domain || null
  })
  return refreshed
}

export const connections = {
  useListIntegrationConnections: () => {
    return useQuery({
      queryKey: [QUERY_KEYS.listIntegrationConnections],
      queryFn: async () => {
        const { data } = await postNamedQuery('integrator', 'list-integration-connections', {})
        return data
      }
    })
  },

  useIntegrationConnectionTemplate: (domain) => {
    return useQuery({
      enabled: !!domain,
      queryKey: ['integrations/get-integration-connection-template', domain],
      queryFn: async () => {
        const { data } = await postNamedQuery('integrator', 'get-integration-connection-template', {
          domain
        })

        return data
      }
    })
  },

  useCreateIntegrationConnection: () => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async (command) => {
        const { data } = await postNamedCommand('integrator', 'create-integration-connection', command)

        return data
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.listIntegrationConnections], refetchType: 'all' }).catch(console.error)
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.listExposedConnections], refetchType: 'all' }).catch(console.error)
      }
    })
  },

  useExchangeConnectionAuthorization: () => {
    return useMutation({
      mutationFn: async (command) => {
        const { data } = await postNamedCommand('integrator', 'exchange-connection-authorization', command)

        return data
      }
    })
  },

  useGetOutstandingIntegrationConnectionRequests: (options = {}) => {
    return useQuery({
      queryKey: ['integrations/get-outstanding-integration-connection-requests'],
      queryFn: async () => {
        const { data, response } = await postNamedQuery('integrator', 'get-outstanding-integration-connection-requests', {
        })

        if (response.status === 204) {
          return null
        }

        return data
      }
    })
  },

  useGetIntegrationConnectionRequestById: (id, options = {}) => {
    return useQuery({
      enabled: !!id,
      queryKey: ['integrations/get-integration-connection-request-by-id', id],
      queryFn: async () => {
        const { data, response } = await postNamedQuery('integrator', 'get-integration-connection-request-by-id', {
          connectionRequestId: id
        })

        if (response.status === 204) {
          return null
        }

        return data
      }
    })
  },

  useResolveIntegrationConnectionRequest: () => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async (command) => {
        const { data } = await postNamedCommand('integrator', 'resolve-connection-request', command)

        return data
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['integrations/get-integration-connection-request-by-id'], refetchType: 'all' }).catch(console.error)
        queryClient.invalidateQueries({ queryKey: ['integrations/get-outstanding-integration-connection-requests'], refetchType: 'all' }).catch(console.error)
      }
    })
  },

  useModifyIntegrationConnection: () => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async (command) => {
        const { data } = await postNamedCommand('integrator', 'modify-integration-connection', command)

        return data
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.listIntegrationConnections], refetchType: 'all' }).catch(console.error)
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.listExposedConnections], refetchType: 'all' }).catch(console.error)
      }
    })
  },

  useListExposedConnections: (domain, options = {}) => {
    return useQuery({
      queryKey: [QUERY_KEYS.listExposedConnections, domain || null],
      queryFn: async () => {
        const { data } = await postNamedQuery('integrator', 'list-exposed-connections', {
          domain: domain || null
        })
        return data
      },
      ...options
    })
  },

  useListUserConnectionTokens: (domain, options = {}) => {
    return useQuery({
      queryKey: [QUERY_KEYS.listUserConnectionTokens, domain || null],
      queryFn: async () => listUserConnectionTokensWithSilentRefresh(domain),
      ...options
    })
  },

  useAuthorizeUserConnectionToken: () => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async (command) => {
        const { data } = await postNamedCommand('integrator', 'authorize-user-connection-token', command)
        return data
      },
      onSuccess: (_data, command) => {
        // Successful reconnect means the refresh token is fresh again - clear
        // the "we gave up trying to refresh this domain" memo so a future
        // silent refresh attempt isn't short-circuited.
        if (command?.domain) refreshExhaustedForDomain.delete(command.domain)
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.listUserConnectionTokens], refetchType: 'all' }).catch(console.error)
      }
    })
  },

  useRevokeUserConnectionToken: () => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async (command) => {
        const { data } = await postNamedCommand('integrator', 'revoke-user-connection-token', command)
        return data
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.listUserConnectionTokens], refetchType: 'all' }).catch(console.error)
      }
    })
  },

  // Force a server-side OAuth refresh of the caller's user-connection token for
  // the given domain. Returns `{ success, reauthRequired, ... }`. Used by
  // IntegrationAuthProvider to auto-recover from an expired access token when
  // the refresh token is still valid - so the user doesn't see "Reconnect"
  // every time their (e.g.) 2-hour Wealthbox access token expires. When
  // `reauthRequired: true` comes back, the refresh token itself is dead and
  // the UI falls through to the standard reconnect prompt.
  useRefreshUserConnectionToken: () => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async (command) => {
        const { data } = await postNamedCommand('integrator', 'refresh-user-connection-token', command)
        return data
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.listUserConnectionTokens], refetchType: 'all' }).catch(console.error)
      }
    })
  }
}

export const artifacts = {
  useListArtifactsPicklist: (connectionId) => {
    return useQuery({
      queryKey: [QUERY_KEYS.listIntegrationArtifactsPicklist, connectionId],
      queryFn: async () => {
        const body = {}
        if (connectionId) body.integrationConnectionId = connectionId
        const { data } = await postNamedQuery('integrator', 'list-integration-artifacts-picklist', body)
        return data
      }
    })
  },

  useGetArtifactById: (artifactId) => {
    return useQuery({
      enabled: !!artifactId,
      queryKey: [QUERY_KEYS.getIntegrationArtifactById, artifactId],
      queryFn: async () => {
        const { data } = await postNamedQuery('integrator', 'get-integration-artifact-by-id', {
          artifactId
        })
        return data
      }
    })
  }
}

export const planning = {
  usePlanningQuery: ({ queryType, payload }) => {
    return useQuery({
      queryKey: [`integrations/query-orion-planning/${queryType}/${JSON.stringify(payload)}`, 'integrator'],
      queryFn: async () => {
        const { data } = await postNamedQuery('integrator', 'query-orion-planning', {
          queryType,
          payload
        })

        return data
      }
    })
  },

  useMontecarlo: ({ clientId, planType }) => {
    return useQuery({
      queryKey: [`integrations/query-orion-planning/montecarlo/${clientId}/${planType}`, 'integrator'],
      queryFn: async () => {
        const { data } = await postNamedQuery('integrator', 'query-orion-planning', {
          queryType: 'montecarlo',
          payload: {
            clientId,
            planType
          }
        })

        return data
      }
    })
  }
}

export const provisionedTargets = {
  useGetProvisionedTargetTableById: (provisionedTargetTableId) => {
    return useQuery({
      enabled: !!provisionedTargetTableId,
      queryKey: [QUERY_KEYS.getProvisionedTargetTableById, provisionedTargetTableId],
      queryFn: async () => {
        const { data } = await postNamedQuery('integrator', 'get-provisioned-target-table-by-id', {
          provisionedTargetTableId
        })
        return data
      }
    })
  },

  useListProvisionedTargetTables: () => {
    return useQuery({
      queryKey: [QUERY_KEYS.listProvisionedTargetTables],
      queryFn: async () => {
        const { data } = await postNamedQuery('integrator', 'list-provisioned-target-tables', {})
        return data
      }
    })
  },

  useListProvisionedTargetPartitions: (provisionedTargetTableId) => {
    return useQuery({
      enabled: !!provisionedTargetTableId,
      queryKey: [QUERY_KEYS.listProvisionedTargetPartitions, provisionedTargetTableId],
      queryFn: async () => {
        const { data } = await postNamedQuery('integrator', 'list-provisioned-target-partitions', {
          provisionedTargetTableId
        })
        return data
      }
    })
  },

  useListJobsByProvisionedTarget: (provisionedTargetTableId) => {
    return useQuery({
      enabled: !!provisionedTargetTableId,
      queryKey: [QUERY_KEYS.listJobsByProvisionedTarget, provisionedTargetTableId],
      queryFn: async () => {
        const { data } = await postNamedQuery('integrator', 'list-jobs-by-provisioned-target', {
          provisionedTargetTableId
        })
        return data
      }
    })
  },

  useCreateProvisionedTargetTable: () => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async (command) => {
        const { data } = await postNamedCommand('integrator', 'create-provisioned-target-table', command)
        return data
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.listProvisionedTargetTables], refetchType: 'all' }).catch(console.error)
      }
    })
  },

  useModifyProvisionedTargetTable: () => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async (command) => {
        const { data } = await postNamedCommand('integrator', 'modify-provisioned-target-table', command)
        return data
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.listProvisionedTargetTables], refetchType: 'all' }).catch(console.error)
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getProvisionedTargetTableById], refetchType: 'all' }).catch(console.error)
      }
    })
  },

  useAssignJobToProvisionedTarget: () => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async (command) => {
        const { data } = await postNamedCommand('integrator', 'assign-job-to-provisioned-target', command)
        return data
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.listJobsByProvisionedTarget], refetchType: 'all' }).catch(console.error)
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getProvisionedTargetTableById], refetchType: 'all' }).catch(console.error)
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.searchIntegrationJobs], refetchType: 'all' }).catch(console.error)
      }
    })
  },

  useUnassignJobFromProvisionedTarget: () => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async (command) => {
        const { data } = await postNamedCommand('integrator', 'unassign-job-from-provisioned-target', command)
        return data
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.listJobsByProvisionedTarget], refetchType: 'all' }).catch(console.error)
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getProvisionedTargetTableById], refetchType: 'all' }).catch(console.error)
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.searchIntegrationJobs], refetchType: 'all' }).catch(console.error)
      }
    })
  },

  usePreviewProvisionedTargetMigration: () => {
    return useMutation({
      mutationFn: async (command) => {
        const { data } = await postNamedCommand('integrator', 'preview-provisioned-target-migration', command)
        return data
      }
    })
  },

  useApplyProvisionedTargetMigration: () => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async (command) => {
        const { data } = await postNamedCommand('integrator', 'apply-provisioned-target-migration', command)
        return data
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getProvisionedTargetTableById], refetchType: 'all' }).catch(console.error)
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.listProvisionedTargetPartitions], refetchType: 'all' }).catch(console.error)
      }
    })
  }
}
